"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ElementNode } from "../types";
import type { LightboxChromeConfig, ResolvedLightboxPublic } from "../ui/types/lightbox";
import { renderBuilderNode } from "./nodeRenderer";
import { normalizeLiveElementForRuntime } from "./normalizeLiveElementForRuntime";
const SIZE_MAX_WIDTH: Record<NonNullable<LightboxChromeConfig["size"]>, string> = {
  sm: "min(24rem, 92vw)",
  md: "min(36rem, 92vw)",
  lg: "min(48rem, 94vw)",
  full: "min(96vw, 72rem)"
};
function focusableIn(root: HTMLElement | null): HTMLElement[] {
  if (!root) {
    return [];
  }
  return [...root.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])')].filter(el => !el.hasAttribute("disabled") && el.tabIndex !== -1);
}
type LightboxDialogProps = {
  lightbox: ResolvedLightboxPublic;
  open: boolean;
  onClose: () => void;
};
function LightboxDialog(t0) {
  const $ = _c(34);
  const {
    lightbox,
    open,
    onClose
  } = t0;
  const titleId = useId();
  const panelRef = useRef(null);
  let t1;
  if ($[0] !== lightbox.config) {
    t1 = lightbox.config ?? {};
    $[0] = lightbox.config;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const config = t1;
  const closeOnOverlay = config.closeOnOverlayClick !== false;
  const showClose = config.showCloseButton !== false;
  const opacity = typeof config.overlayOpacity === "number" ? config.overlayOpacity : 0.5;
  const maxWidth = SIZE_MAX_WIDTH[config.size ?? "md"];
  let t2;
  let t3;
  if ($[2] !== onClose || $[3] !== open) {
    t2 = () => {
      if (!open) {
        return;
      }
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const onKey = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", onKey);
      const panel = panelRef.current;
      const focusables = focusableIn(panel);
      const first = focusables[0];
      first?.focus();
      return () => {
        document.body.style.overflow = prev;
        document.removeEventListener("keydown", onKey);
      };
    };
    t3 = [open, onClose];
    $[2] = onClose;
    $[3] = open;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (!open) {
    return null;
  }
  const root = lightbox.schema as {
    root?: ElementNode;
  } | ElementNode;
  const bodyNode = root && typeof root === "object" && "root" in root ? (root as {
    root?: ElementNode;
  }).root : root as ElementNode | undefined;
  let t4;
  if ($[6] !== bodyNode) {
    t4 = bodyNode ? renderBuilderNode(normalizeLiveElementForRuntime(bodyNode), {}) : null;
    $[6] = bodyNode;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const body = t4;
  const t5 = `rgba(0,0,0,${opacity})`;
  let t6;
  if ($[8] !== t5) {
    t6 = {
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "1rem",
      backgroundColor: t5
    };
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = closeOnOverlay ? onClose : undefined;
  const t8 = closeOnOverlay ? "pointer" : "default";
  let t9;
  if ($[10] !== t8) {
    t9 = {
      position: "absolute",
      inset: 0,
      border: "none",
      background: "transparent",
      cursor: t8
    };
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t7 || $[13] !== t9) {
    t10 = <button aria-label="Close dialog overlay" onClick={t7} style={t9} tabIndex={-1} type="button" />;
    $[12] = t7;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== maxWidth) {
    t11 = {
      position: "relative",
      zIndex: 1,
      maxWidth,
      width: "100%",
      maxHeight: "min(90vh, 48rem)",
      overflow: "auto",
      background: "var(--tt-color-bg, #fff)",
      color: "var(--tt-color-fg, inherit)",
      borderRadius: "var(--radius-md, 8px)",
      boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
      padding: "1.25rem"
    };
    $[15] = maxWidth;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== lightbox.slug || $[18] !== titleId) {
    t12 = <span className="sr-only" id={titleId}>{lightbox.slug}</span>;
    $[17] = lightbox.slug;
    $[18] = titleId;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== onClose || $[21] !== showClose) {
    t13 = showClose ? <button aria-label="Close" onClick={onClose} style={{
      position: "absolute",
      top: 8,
      right: 8,
      border: "none",
      background: "transparent",
      cursor: "pointer",
      fontSize: "1.25rem",
      lineHeight: 1,
      color: "var(--tt-color-muted, #64748b)"
    }} type="button">×</button> : null;
    $[20] = onClose;
    $[21] = showClose;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== body || $[24] !== t11 || $[25] !== t12 || $[26] !== t13 || $[27] !== titleId) {
    t14 = <div aria-labelledby={titleId} aria-modal="true" className="pb-lightbox-panel" ref={panelRef} role="dialog" style={t11}>{t12}{t13}{body}</div>;
    $[23] = body;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = titleId;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== lightbox.id || $[30] !== t10 || $[31] !== t14 || $[32] !== t6) {
    t15 = <div aria-hidden={false} className="pb-lightbox-root" data-lightbox-overlay={lightbox.id} style={t6}>{t10}{t14}</div>;
    $[29] = lightbox.id;
    $[30] = t10;
    $[31] = t14;
    $[32] = t6;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}
export type LightboxPublicControllerProps = {
  lightboxes: ResolvedLightboxPublic[];
};

/**
 * Client controller: opens modals when triggers with `data-lightbox-id` are clicked.
 * Mount once per page beside SSR content. Consent gating for scripts inside modal
 * body is deferred — host must not inject non-essential scripts without consent.
 */
export function LightboxPublicController({
  lightboxes
}: LightboxPublicControllerProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const byId = useRef(new Map(lightboxes.map(lb => [lb.id, lb])));
  byId.current = new Map(lightboxes.map(lb_0 => [lb_0.id, lb_0]));
  const onDocClick = useCallback((e: MouseEvent) => {
    const target = e.target;
    if (!(target instanceof Element)) {
      return;
    }
    const trigger = target.closest("[data-lightbox-id]");
    if (!trigger) {
      return;
    }
    const id = trigger.getAttribute("data-lightbox-id")?.trim();
    if (!(id && byId.current.has(id))) {
      return;
    }
    e.preventDefault();
    setOpenId(id);
  }, []);
  useEffect(() => {
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [onDocClick]);
  const active = openId ? byId.current.get(openId) : undefined;
  return active ? <LightboxDialog lightbox={active} onClose={() => setOpenId(null)} open={Boolean(openId)} /> : null;
}